import * as THREE from 'three';

import { EulerOrder, type Skeleton } from '../skeleton/core';
import { SCALE_IDENTITY_EPSILON as SCALE_EPSILON } from '../skeleton/boneTransforms';
import {
  eulerDegreesFromQuaternion,
  normalizeQuaternion,
} from '../skeleton/rotationService';

export const BONE_OUTLINER_NODE_PREFIX = 'bone:';
export const TRANSFORM_OUTLINER_NODE_PREFIX = 'transform:';

const TRANSLATION_EPSILON = 1e-4;
const ROTATION_EPSILON_DEGREES = 0.05;

type OutlinerNodeBase = {
  id: string;
  name: string;
  parentId: string | null;
  childrenIds: string[];
};

export type OutlinerBoneNode = OutlinerNodeBase & {
  kind: 'bone';
  boneIndex: number;
};

export type TransformOffsetSummary = {
  translation: [number, number, number];
  rotation: [number, number, number];
  scale: [number, number, number];
};

export type OutlinerTransformNode = OutlinerNodeBase & {
  kind: 'transform';
  localMatrix: THREE.Matrix4;
  offset: TransformOffsetSummary;
  offsetLabel: string | null;
};

export type OutlinerHierarchyNode = OutlinerBoneNode | OutlinerTransformNode;

export type OutlinerHierarchy = {
  rootIds: string[];
  nodesById: Record<string, OutlinerHierarchyNode>;
  boneNodeIdByIndex: string[];
};

function getObjectLocalMatrix(object: THREE.Object3D): THREE.Matrix4 {
  if (object.matrix) return object.matrix.clone();
  return new THREE.Matrix4().compose(
    object.position.clone(),
    object.quaternion.clone(),
    object.scale.clone(),
  );
}

function cleanNumber(value: number, precision: number): number {
  const rounded = Number(value.toFixed(precision));
  return Object.is(rounded, -0) ? 0 : rounded;
}

function isNearIdentityMatrix(matrix: THREE.Matrix4): boolean {
  const e = matrix.elements;
  return (
    Math.abs(e[0] - 1) <= SCALE_EPSILON &&
    Math.abs(e[5] - 1) <= SCALE_EPSILON &&
    Math.abs(e[10] - 1) <= SCALE_EPSILON &&
    Math.abs(e[12]) <= TRANSLATION_EPSILON &&
    Math.abs(e[13]) <= TRANSLATION_EPSILON &&
    Math.abs(e[14]) <= TRANSLATION_EPSILON &&
    Math.abs(e[1]) <= TRANSLATION_EPSILON &&
    Math.abs(e[2]) <= TRANSLATION_EPSILON &&
    Math.abs(e[4]) <= TRANSLATION_EPSILON &&
    Math.abs(e[6]) <= TRANSLATION_EPSILON &&
    Math.abs(e[8]) <= TRANSLATION_EPSILON &&
    Math.abs(e[9]) <= TRANSLATION_EPSILON
  );
}

function buildTransformOffsetSummary(localMatrix: THREE.Matrix4): TransformOffsetSummary {
  const position = new THREE.Vector3();
  const quaternion = new THREE.Quaternion();
  const scale = new THREE.Vector3();
  localMatrix.decompose(position, quaternion, scale);
  normalizeQuaternion(quaternion);

  const [rx, ry, rz] = eulerDegreesFromQuaternion(quaternion, EulerOrder.XYZ);

  return {
    translation: [
      cleanNumber(position.x, 3),
      cleanNumber(position.y, 3),
      cleanNumber(position.z, 3),
    ],
    rotation: [cleanNumber(rx, 2), cleanNumber(ry, 2), cleanNumber(rz, 2)],
    scale: [cleanNumber(scale.x, 4), cleanNumber(scale.y, 4), cleanNumber(scale.z, 4)],
  };
}

export function formatTransformOffsetSummary(offset: TransformOffsetSummary): string | null {
  const [tx, ty, tz] = offset.translation;
  const [rx, ry, rz] = offset.rotation;
  const [sx, sy, sz] = offset.scale;

  const labels: string[] = [];
  if (Math.abs(tx) > TRANSLATION_EPSILON) labels.push(`Tx ${tx}`);
  if (Math.abs(ty) > TRANSLATION_EPSILON) labels.push(`Ty ${ty}`);
  if (Math.abs(tz) > TRANSLATION_EPSILON) labels.push(`Tz ${tz}`);
  if (Math.abs(rx) > ROTATION_EPSILON_DEGREES) labels.push(`Rx ${rx}`);
  if (Math.abs(ry) > ROTATION_EPSILON_DEGREES) labels.push(`Ry ${ry}`);
  if (Math.abs(rz) > ROTATION_EPSILON_DEGREES) labels.push(`Rz ${rz}`);
  if (Math.abs(sx - 1) > SCALE_EPSILON) labels.push(`Sx ${sx}`);
  if (Math.abs(sy - 1) > SCALE_EPSILON) labels.push(`Sy ${sy}`);
  if (Math.abs(sz - 1) > SCALE_EPSILON) labels.push(`Sz ${sz}`);

  return labels.length > 0 ? labels.join(', ') : null;
}

export function getBoneOutlinerNodeId(index: number): string {
  return `${BONE_OUTLINER_NODE_PREFIX}${index}`;
}

export function getTransformOutlinerNodeId(object: THREE.Object3D): string {
  return `${TRANSFORM_OUTLINER_NODE_PREFIX}${object.uuid}`;
}

export function getTransformNodeName(object: THREE.Object3D): string {
  const trimmedName = object.name.trim();
  if (trimmedName) return trimmedName;
  return `Transform_${object.uuid.slice(0, 8)}`;
}

export function shouldIncludeTransformObject(object: THREE.Object3D): boolean {
  if (object instanceof THREE.Bone) return false;
  if (object.name.trim().length > 0) return true;
  const localMatrix = getObjectLocalMatrix(object);
  return !isNearIdentityMatrix(localMatrix);
}

export function createTransformOutlinerNode(
  object: THREE.Object3D,
  localMatrixOverride?: THREE.Matrix4,
): OutlinerTransformNode {
  const localMatrix = localMatrixOverride?.clone() ?? getObjectLocalMatrix(object);
  const offset = buildTransformOffsetSummary(localMatrix);
  return {
    kind: 'transform',
    id: getTransformOutlinerNodeId(object),
    name: getTransformNodeName(object),
    parentId: null,
    childrenIds: [],
    localMatrix,
    offset,
    offsetLabel: formatTransformOffsetSummary(offset),
  };
}

export function buildBoneOnlyOutlinerHierarchy(skeleton: Skeleton): OutlinerHierarchy {
  const nodesById: Record<string, OutlinerHierarchyNode> = {};
  const boneNodeIdByIndex: string[] = [];

  for (const bone of skeleton.bones) {
    const id = getBoneOutlinerNodeId(bone.index);
    boneNodeIdByIndex[bone.index] = id;
    nodesById[id] = {
      kind: 'bone',
      id,
      name: bone.name,
      boneIndex: bone.index,
      parentId: null,
      childrenIds: [],
    };
  }

  const rootIds: string[] = [];
  for (const bone of skeleton.bones) {
    const nodeId = boneNodeIdByIndex[bone.index];
    const node = nodesById[nodeId];
    if (!node || node.kind !== 'bone') continue;

    if (bone.parent === null) {
      rootIds.push(nodeId);
      continue;
    }

    const parentNodeId = boneNodeIdByIndex[bone.parent];
    if (!parentNodeId) {
      rootIds.push(nodeId);
      continue;
    }

    node.parentId = parentNodeId;
    const parentNode = nodesById[parentNodeId];
    if (parentNode) parentNode.childrenIds.push(nodeId);
  }

  return { rootIds, nodesById, boneNodeIdByIndex };
}
